import React, { memo, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';
import { setToaster } from 'Containers/Core/actions';
import { Modal } from 'Components/Modal';
import { SpinnerBlock } from 'Components/SpinnerBlock';
import { AssetPlacementList } from 'Components/SerializedEquipment';

import { getAssetPlacements, updatePlacement, deletePlacement } from '../actions';
import { AssetPlacement, EquipmentAsset } from '../types';

interface Props {
  isOpen: boolean;
  asset: EquipmentAsset | null;
  onClose: (e: any) => void;
}

// Per-unit placement history for a serialized asset (GET /equipment-assets/{id}/placements).
function AssetHistoryModalContainer({ isOpen, asset, onClose }: Props) {
  const dispatch = useDispatch();
  const [placements, setPlacements] = useState<AssetPlacement[]>([]);
  const [fetching, setFetching] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!asset) return;
    setFetching(true);
    const response: any = await dispatch(getAssetPlacements(asset.id));
    setPlacements(response?.data ?? []);
    setFetching(false);
  }, [asset, dispatch]);

  useEffect(() => {
    if (isOpen && asset) load();
  }, [isOpen, asset, load]);

  useEffect(() => {
    if (!isOpen) setPlacements([]);
  }, [isOpen]);

  // Correct a stay's dates. Optimistic-locked on the placement's own updated_at; on any failure we
  // reload the list and surface the backend 409/422 message.
  const onSaveDates = useCallback(
    async (placement: AssetPlacement, payload: { date_in?: string; date_out?: string }) => {
      setSavingId(placement.id);
      const response: any = await dispatch(updatePlacement(placement.id, { ...payload, updated_at: placement.updated_at }));
      setSavingId(null);
      if (response?.data) {
        load();
        return;
      }
      if (response?.error?.status === 409) load();
      dispatch(setToaster(response?.error?.message || 'Couldn’t update the dates. Please try again.', false));
    },
    [dispatch, load]
  );

  // Delete a historical (closed) record. 204 → success is a string (delete quirk); { error } on
  // failure (e.g. 422 for an active placement). Reloads history + surfaces the backend message.
  const onDeletePlacement = useCallback(
    async (placement: AssetPlacement) => {
      setDeletingId(placement.id);
      const response: any = await dispatch(deletePlacement(placement.id));
      setDeletingId(null);
      if (typeof response === 'string') {
        load();
        return;
      }
      dispatch(setToaster(response?.error?.message || 'Couldn’t delete this record. Please try again.', false));
    },
    [dispatch, load]
  );

  return (
    <Modal
      id="asset-history-modal"
      title="Placement History"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <div className="d-flex flex-column w-100">
        <p className="text-muted">{asset?.name ?? 'Asset'}</p>
        <SpinnerBlock fetching={fetching} />
        {!fetching && (
          <AssetPlacementList
            placements={placements}
            onSaveDates={onSaveDates}
            savingId={savingId}
            onDeletePlacement={onDeletePlacement}
            deletingId={deletingId}
          />
        )}
      </div>
    </Modal>
  );
}

const AssetHistoryModalMemo = memo(AssetHistoryModalContainer, areEqual);

export { AssetHistoryModalMemo as AssetHistoryModal };
